import GetEventRegistrantCounts from '@/connection/audiences/event-registrant-counts';
import type { ProjectRegistrantCounts } from '@/types/registration.types';

class Loader {
  loading: Set<string> = new Set();
  callbacks: Map<string, ((counts: ProjectRegistrantCounts | undefined, error: Error | undefined) => void)[]> =
    new Map();
  data: Map<string, [ProjectRegistrantCounts, number]> = new Map();

  async load(
    props: { channel: number; projectUuid: string },
    callback: (counts: ProjectRegistrantCounts | undefined, error: Error | undefined) => void
  ) {
    // check if we are already loading the counts for this event, if so queue the callback
    if (this.loading.has(props.projectUuid)) {
      this.callbacks.set(props.projectUuid, [...(this.callbacks.get(props.projectUuid) ?? []), callback]);
      return;
    }

    // create the loading set and begin loading the data
    this.loading.add(props.projectUuid);
    this.callbacks.set(props.projectUuid, [callback]);

    try {
      // load the data and reply to all callbacks with the same data
      const response = await GetEventRegistrantCounts(props);
      this.callbacks.get(props.projectUuid)?.forEach((cb) => cb(response, undefined));

      // cache the data for another request
      this.data.set(props.projectUuid, [response, Date.now()]);
    } catch (e) {
      console.error(e);

      // on error, reject all the callbacks
      this.callbacks
        .get(props.projectUuid)
        ?.forEach((cb) => cb(undefined, e instanceof Error ? e : new Error(String(e))));
    } finally {
      // drop all the callbacks and end the load
      this.callbacks.delete(props.projectUuid);
      this.loading.delete(props.projectUuid);
    }
  }

  get(props: { channel: number; projectUuid: string }) {
    return new Promise<ProjectRegistrantCounts>((resolve, reject) => {
      const data = this.data.get(props.projectUuid);
      if (data) {
        const [counts, loadedAt] = data;

        // return existing data if it's less than 1 minute old
        if (loadedAt > Date.now() - 1000 * 60) {
          resolve(counts);
          return;
        }
      }

      this.load(props, (counts, error) => {
        if (counts) resolve(counts);
        else reject(error ?? new Error(`Unable to load counts`));
      });
    });
  }
}

export default new Loader();
